import {
  ControllerStateAndHelpers,
} from 'downshift';
import { GetString } from 'fluent-react';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { VisualProps } from './Dropdown';
import {
  OptionWithHeight,
} from './Dropdown';
import Menu from './Menu';
import Prompt from './Prompt';
import TriggerButton from './TriggerButton';

const RootContainer = styled.div<{visualProps: VisualProps}>`
  width: ${({visualProps}) => visualProps.totalWidth}rem;
  ${({visualProps}) =>
    visualProps.heightAttr + ': ' + visualProps.searchBarHeight + visualProps.searchBarHeightUnit
  };
  position: relative;
`;

// Note: the value of `refKeyOnRoot` needs to match the corresponding key name in `RootComponentProps`:
const refKeyOnRoot = 'rootRefKey';
interface DropdownRootProps {
  visualProps: VisualProps;
  rootProps: {
    rootRefKey: (el: HTMLDivElement | null) => void,
  };
}

const DropdownRoot: React.FunctionComponent<DropdownRootProps> = (props) => {
  const {
    visualProps,
    rootProps: {rootRefKey, ...restOfRootProps},
  } = props;
  const elRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const {current: el} = elRef;
    rootRefKey(el);
    if (el !== null) {
      // Block arrow up/down/left/right arrow keydown events and mouse wheel
      // events from bubbling up to the `document` to avoid triggering the
      // prev/next slide navigation. The functionalities of the up/down keys are
      // restored in the `Prompt` component.
      // Block ESC, shift key causing entire app to crash when dropdown is open
      // Block all non letter keys from causing entire app to crash
      const stopArrowKeyEventPropagation = (e: KeyboardEvent) => {
        if (
            e.keyCode === 37 ||
            e.keyCode === 38 ||
            e.keyCode === 39 ||
            e.keyCode === 40 ||
            e.keyCode === 27 ||
            e.keyCode === 16) {
          e.stopPropagation();
        } else if (!((e.keyCode >= 65 && e.keyCode <= 90) || e.keyCode === 8 || e.keyCode === 13)) {
          e.preventDefault();
        }
      };
      const stopWheelEventPropagation = (e: WheelEvent) => {
        e.stopPropagation();
      };
      el.addEventListener('keydown', stopArrowKeyEventPropagation);
      el.addEventListener('wheel', stopWheelEventPropagation);
      return () => {
        el.removeEventListener('keydown', stopArrowKeyEventPropagation);
        el.removeEventListener('wheel', stopWheelEventPropagation);
      };
    }
  });

  return (
    <RootContainer visualProps={visualProps} {...restOfRootProps} ref={elRef}>
      {props.children}
    </RootContainer>
  );
};

interface OuterInput {
  options: OptionWithHeight[];
  promptText: string;
  getFluentString: GetString;
  useTriggerButton: boolean;
  selectedItem: OptionWithHeight;
  inputKeydownHandler: (event: KeyboardEvent) => void;
  visualProps: VisualProps;
}

const getRenderProp =
  (outerInput: OuterInput) => (innerInput: ControllerStateAndHelpers<OptionWithHeight>) => {

  const {
    promptText, selectedItem, options, inputKeydownHandler, visualProps,
    getFluentString, useTriggerButton,
  } = outerInput;
  const {
    getInputProps, getItemProps, getMenuProps, getRootProps,
    highlightedIndex, isOpen, openMenu,
  } = innerInput;

  // Set `suppressRefError` to `true` because we'll manually call the ref function
  // inside `RootComponent`:
  const rootProps = getRootProps({refKey: refKeyOnRoot}, {suppressRefError: true});

  let prompt: React.ReactElement<any>;
  let menu: React.ReactElement<any>;
  if (isOpen === true) {
    prompt = useTriggerButton === false ? (
      <Prompt
        isLoading={false}
        isMenuOpen={true}
        promptText={promptText}
        getInputProps={getInputProps}
        inputKeydownHandler={inputKeydownHandler}
        visualProps={visualProps}
      />
    ) : (
      <TriggerButton
        isLoading={false}
        isMenuOpen={true}
        promptText={getFluentString('country-selector-prompt')}
        getInputProps={getInputProps}
        inputKeydownHandler={inputKeydownHandler}
        visualProps={visualProps}
      />
    );
    // The `refKey` *has* to be `innerRef` because react-window will pass that ref
    // down to the DOM element enclosing the dropdown list:
    const menuProps = getMenuProps({refKey: 'innerRef'});
    menu = (
      <Menu
        menuProps={menuProps}
        getItemProps={getItemProps}
        items={options}
        highlightedIndex={highlightedIndex}
        visualProps={visualProps}
        selectedItemValue={selectedItem.value}
        useTriggerButton={useTriggerButton}
      />
    );
  } else {
    prompt = useTriggerButton === false ? (
      <Prompt
        isLoading={false}
        isMenuOpen={false}
        selectedItem={selectedItem}
        openMenu={openMenu}
        getInputProps={getInputProps}
        inputKeydownHandler={inputKeydownHandler}
        visualProps={visualProps}
      />
     ) : (
        <TriggerButton
          isLoading={false}
          isMenuOpen={false}
          promptText={getFluentString('conclusion-slide-text-search-another-country')}
          openMenu={openMenu}
          getInputProps={getInputProps}
          inputKeydownHandler={inputKeydownHandler}
          visualProps={visualProps}
        />
      );
    const menuProps = getMenuProps();
    menu = (<div {...menuProps} style={{display: 'none'}}/>);
  }

  return (
    <DropdownRoot visualProps={visualProps} rootProps={rootProps}>
      {prompt}
      {menu}
    </DropdownRoot>
  );
};

export default getRenderProp;
